import { DateTime } from '@ksyos/date-time';
import type { OutpatientClinicTask as OutpatientClinicTaskModel } from '@common/contexts/support/domain/models/outpatientClinicTaskModel';
import { getDisplayName } from '@common/utils/getDisplayName';
import { formatRelativeDate } from '@frontend/components/pages/outpatientClinic/utils/formatRelativeDate';
import { getSelectedLanguage } from '@frontend/translate';
import { getPatientTaskDescriptionOrType } from '@frontend/utils';
import type { FormattedOutpatientClinicTask } from '../types/formattedOutpatientClinicTask';

export function formatOutpatientClinicTask(item: OutpatientClinicTaskModel): FormattedOutpatientClinicTask {
    const now = DateTime.now();

    return {
        ...item,
        availability: parseAvailability(item),
        birthDate: formatRelativeDate(item.patient.birthDate, now, true),
        category: {
            id: item.type.category.id,
            name: item.type.category.name,
        },
        description: getPatientTaskDescriptionOrType(item, item.episodeCategory),
        expectedDateCompleted: formatRelativeDate(item.expectedDateCompleted, now, true),
        isFlexibleCarePath: item.isFlexibleCarePath,
        isOverdue: DateTime.from(item.expectedDateCompleted).isBefore(now),
        patient: {
            id: item.patient.id,
        },
        patientName: getDisplayName(item.patient),
        performerOrganization: {
            id: item.performerOrganization.id,
            name: item.performerOrganization.name,
        },
        performerPractitioner: parsePerformerPractitioner(item),
        performerPractitionerProfession: parsePerformerPractitionerProfession(item),
        updated_at: formatRelativeDate(item.updated_at, now, true),
    };
}

function parseAvailability(item: OutpatientClinicTaskModel): {
    firstTimeRange?: string;
    secondTimeRange?: string;
} {
    if (!item.customControlData || !item.customControlData.availability) {
        return {
            firstTimeRange: undefined,
            secondTimeRange: undefined,
        };
    }

    const firstTimeRange =
        item.customControlData?.availability?.firstTimeRange.startTime &&
        item.customControlData?.availability?.firstTimeRange.endTime
            ? item.customControlData?.availability?.firstTimeRange
            : undefined;

    const secondTimeRange =
        item.customControlData?.availability?.secondTimeRange.startTime &&
        item.customControlData?.availability?.secondTimeRange.endTime
            ? item.customControlData?.availability?.secondTimeRange
            : undefined;

    return {
        firstTimeRange: firstTimeRange ? `${firstTimeRange.startTime} - ${firstTimeRange.endTime}` : undefined,
        secondTimeRange: secondTimeRange ? `${secondTimeRange.startTime} - ${secondTimeRange.endTime}` : undefined,
    };
}

function parsePerformerPractitioner(item: OutpatientClinicTaskModel): string | undefined {
    if (!item.performerPractitioner) {
        return undefined;
    }

    const practitioner = getDisplayName(item.performerPractitioner);

    return practitioner === ' ' ? undefined : practitioner;
}

function parsePerformerPractitionerProfession(item: OutpatientClinicTaskModel): string | undefined {
    const translation = getSelectedLanguage();
    const performerPractitionerProfession = item.performerPractitionerProfession
        ? translation.processActorProfession[
              item.performerPractitionerProfession as keyof typeof translation.processActorProfession
          ]
        : undefined;

    return performerPractitionerProfession;
}
